import type { TFunction } from 'i18next';
import { array, type InferType, mixed, object, string } from 'yup';
import { getAudienceShareItemSchema } from '../../utils/validationSchema';
import type { ExportTypeValue } from '../constants';
import { exportType } from '../constants';
import type { AudienceForm } from '../types';
import { hasInternationalCriteriaInDefinition } from './hasInternationalCriteria';
import { getShouldUseRegionFirstFlow, shouldShowTargetIdTypes } from './marketOperatorRestrictions';
import { validateNormalizedDefinition } from './validateNormalizedDefinition';

export type AudienceFormSchemaOptions = {
  isCreate?: boolean;
  isCreateFromTemplate?: boolean;
};

type AudienceFormValidationShape = {
  normalized_definition: AudienceForm['normalized_definition'] | undefined;
  account_id?: unknown;
};

const hasAudienceValidationShape = (value: unknown): value is AudienceFormValidationShape => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  return 'normalized_definition' in value;
};

export const getBaseAudienceFormSchema = (t: TFunction, options?: AudienceFormSchemaOptions) =>
  object({
    account_id: string().required(t('Account ID is required')),
    export_type: mixed<ExportTypeValue>().required(t('Export Type is required')),
    name: string()
      .required(t('Audience Name is required'))
      .max(255, t('Audience Name cannot be longer than 255 characters')),
    description: string().defined().max(1000, t('Audience description cannot be longer than 1,000 characters')),
    export_targets: mixed<Record<string, boolean>>()
      .required()
      .test('at-least-one-export-target', function (value) {
        const { export_type: exportTypeValue, normalized_definition: normalizedDefinition } = this.parent;

        if (!shouldShowTargetIdTypes(exportTypeValue, normalizedDefinition)) {
          return true;
        }

        const hasSelectedTarget = !!value && Object.values(value).some(Boolean);

        if (hasSelectedTarget) {
          return true;
        }

        return this.createError({
          message: t(
            'Export target ID types cannot be empty, resulting in empty audience. Select at least one ID type to continue.'
          ),
        });
      }),
    direct_audience_provider: string()
      .nullable()
      .defined()
      .when('export_type', {
        is: (val: ExportTypeValue) => val === exportType.BIDSTREAM || val === exportType.BIDSTREAM_PLUS,
        then: s => s.required(t('Direct Audience Provider is required')),
        otherwise: s => s.nullable(),
      }),
    normalized_definition: mixed<AudienceForm['normalized_definition']>().nullable(),
  }).test('normalized_definition_validation', function (formValues) {
    if (!hasAudienceValidationShape(formValues) || !formValues.normalized_definition) return true;

    const normalizedDefinition = formValues.normalized_definition;
    const shouldUseRegionFirstFlow = getShouldUseRegionFirstFlow({
      isCreate: options?.isCreate,
      isCreateFromTemplate: options?.isCreateFromTemplate,
      hasInternationalCriteria: hasInternationalCriteriaInDefinition(normalizedDefinition),
    });

    const validationResult = validateNormalizedDefinition(normalizedDefinition, t, {
      accountId: typeof formValues.account_id === 'string' ? formValues.account_id : '',
      shouldUseRegionFirstFlow,
    });

    if (!validationResult.isValid && validationResult.error) {
      return this.createError({
        path: validationResult.error.path,
        message: validationResult.error.message,
      });
    }

    return true;
  });

export type AudienceFormBaseValues = InferType<ReturnType<typeof getBaseAudienceFormSchema>>;

export const getEditAudienceFormSchema = (t: TFunction, options?: AudienceFormSchemaOptions) => {
  const base = getBaseAudienceFormSchema(t, { ...options, isCreate: false, isCreateFromTemplate: false });
  const editOnly = object({
    audience_share: array(getAudienceShareItemSchema(t)).required(),
  });

  return base.concat(editOnly);
};

export type AudienceFormEditValues = InferType<ReturnType<typeof getEditAudienceFormSchema>>;
